<template>
    <div class="container admin-table-page">
        <!-- HEADER -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- FILTROS POR RANGO DE FECHAS -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center flex-wrap">
                    <label class="filter-label mr-1">{{ labels.from }}:</label>
                    <b-form-input
                        id="fecha-desde"
                        v-model="fechaDesde"
                        type="date"
                        class="filter-date"
                    />

                    <label class="filter-label ml-3 mr-1">{{ labels.to }}:</label>
                    <b-form-input
                        id="fecha-hasta"
                        v-model="fechaHasta"
                        type="date"
                        class="filter-date"
                    />

                    <b-button
                        class="consult-btn ml-3"
                        @click="consultarDatos"
                        :disabled="isLoading"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>

                    <b-button
                        class="clear-btn ml-2"
                        variant="outline-secondary"
                        @click="limpiarFechas"
                        :disabled="isLoading || (!fechaDesde && !fechaHasta)"
                    >
                        <font-awesome-icon :icon="['fas', 'times']" class="mr-1" />
                        {{ labels.clear }}
                    </b-button>
                </div>
            </div>
        </div>

        <div class="admin-table-block mt-4">
            <!-- LOADING INICIAL -->
            <div v-if="isLoading && !fgl_consulta" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>

            <!-- TABLA -->
            <div v-if="fgl_consulta">
                <CorporateDataTable
                    :items="itemsFiltrados"
                    :fields="camposTabla"
                    :labels="tableLabels"
                    :default-per-page="perPage"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="490"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="tableFilterDefs"
                    :toolbar-disabled="false"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                >
                    <template #toolbar-actions>
                        <div class="kardex-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-refresh-btn mr-1"
                                @click="consultarDatos"
                                :disabled="isLoading"
                            >
                                <font-awesome-icon :icon="['fas', 'sync']" class="mr-1" />
                                {{ labels.refresh }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="table-toolbar-export-btn"
                                @click="exportarExcel"
                                :disabled="trazabilidadList.length === 0"
                            >
                                <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-1" />
                                {{ labels.exportExcel }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(fecha)="data">
                        <div class="text-center">{{ formatFecha(data.item.fecha) }}</div>
                    </template>

                    <template #cell(usuario)="data">
                        <div class="text-left">
                            {{ data.item.user_nombre || data.item.user_email || '-' }}
                        </div>
                    </template>

                    <template #cell(campo)="data">
                        <div class="text-left">
                            <span class="campo-badge">{{ data.item.campo }}</span>
                        </div>
                    </template>

                    <template #cell(valor_antes)="data">
                        <div class="text-left valor-cell">
                            {{ data.item.valor_antes || '-' }}
                        </div>
                    </template>

                    <template #cell(valor_despues)="data">
                        <div class="text-left valor-cell">
                            {{ data.item.valor_despues || '-' }}
                        </div>
                    </template>

                    <template #cell(comentario)="data">
                        <div class="text-left comentario-cell">
                            {{ data.item.comentario || '-' }}
                        </div>
                    </template>
                </CorporateDataTable>
            </div>
        </div>
    </div>
</template>

<script>
import TrazabilidadService from '@/services/Trazabilidad'
import CorporateDataTable from '../utils/CorporateDataTable.vue'
import * as XLSX from "exceljs"

export default {
    name: "Trazabilidad",
    components: {
        CorporateDataTable
    },
    data() {
        return {
            // Datos
            trazabilidadList: [],

            // Filtros de fecha (rango)
            fechaDesde: null,
            fechaHasta: null,

            // Estado
            isLoading: false,
            fgl_consulta: false,

            // Búsqueda y paginación
            perPage: 10,
            sortBy: 'fecha',
            sortDesc: true
        }
    },
    computed: {
        labels() {
            if (this.$store.state._language == "es-ES") {
                return {
                    title: "Trazabilidad",
                    from: "Desde",
                    to: "Hasta",
                    consultar: "Consultar",
                    clear: "Limpiar",
                    refresh: "Actualizar",
                    exportExcel: "Exportar Excel",
                    noData: "No hay datos disponibles",
                    noMatches: "No se encontraron resultados",
                    loading: "Cargando...",
                    searchPlaceholder: "Buscar en la tabla...",
                    filterCampo: "Filtrar campo...",
                    filterComentario: "Filtrar comentario...",
                    filterUsuario: "Filtrar usuario..."
                }
            } else {
                return {
                    title: "Traceability",
                    from: "From",
                    to: "To",
                    consultar: "Search",
                    clear: "Clear",
                    refresh: "Refresh",
                    exportExcel: "Export Excel",
                    noData: "No data available",
                    noMatches: "No results found",
                    loading: "Loading...",
                    searchPlaceholder: "Search in table...",
                    filterCampo: "Filter field...",
                    filterComentario: "Filter comment...",
                    filterUsuario: "Filter user..."
                }
            }
        },
        camposTabla() {
            return [
                { key: 'fecha',        label: 'Fecha',          sortable: true,  thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'usuario',      label: 'Usuario',        sortable: true,  thClass: 'text-left',   tdClass: 'text-left align-middle' },
                { key: 'campo',        label: 'Campo',          sortable: true,  thClass: 'text-left',   tdClass: 'text-left align-middle' },
                { key: 'valor_antes',  label: 'Valor Antes',    sortable: false, thClass: 'text-left',   tdClass: 'text-left align-middle' },
                { key: 'valor_despues',label: 'Valor Después',  sortable: false, thClass: 'text-left',   tdClass: 'text-left align-middle' },
                { key: 'comentario',   label: 'Comentario',     sortable: true,  thClass: 'text-left',   tdClass: 'text-left align-middle' }
            ]
        },
        tableFilterDefs() {
            return [
                { key: 'usuario',    type: 'text', placeholder: this.labels.filterUsuario },
                { key: 'campo',      type: 'text', placeholder: this.labels.filterCampo },
                { key: 'comentario', type: 'text', placeholder: this.labels.filterComentario }
            ]
        },
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: "Filas por página:",
                noSort: "Sin orden",
                sortAsc: "A-Z / Menor a mayor",
                sortDesc: "Z-A / Mayor a menor",
                columns: "Columnas",
                showAllColumns: "Mostrar todas",
                filters: "Filtros",
                clearFilters: "Limpiar filtros"
            }
        },
        itemsFiltrados() {
            let items = this.trazabilidadList

            if (this.fechaDesde) {
                const desde = new Date(this.fechaDesde + 'T00:00:00')
                items = items.filter(item => {
                    const f = new Date(item.fecha)
                    return f >= desde
                })
            }

            if (this.fechaHasta) {
                const hasta = new Date(this.fechaHasta + 'T23:59:59')
                items = items.filter(item => {
                    const f = new Date(item.fecha)
                    return f <= hasta
                })
            }

            return items
        }
    },
    mounted() {
        this.consultarDatos()
    },
    methods: {
        goBack() {
            this.$router.back()
        },

        async consultarDatos() {
            try {
                this.isLoading = true
                const response = await TrazabilidadService.getAllTrazabilidad()
                this.trazabilidadList = response.data.listTrazabilidad || []
                this.fgl_consulta = true
            } catch (error) {
                console.error('Error al consultar trazabilidad:', error)
                this.$toast.error('Error al consultar los datos')
                this.trazabilidadList = []
                this.fgl_consulta = true
            } finally {
                this.isLoading = false
            }
        },

        limpiarFechas() {
            this.fechaDesde = null
            this.fechaHasta = null
        },

        formatFecha(fecha) {
            if (!fecha) return '-'
            const d = new Date(fecha)
            if (isNaN(d.getTime())) return fecha
            const anio = d.getFullYear()
            const mes = String(d.getMonth() + 1).padStart(2, '0')
            const dia = String(d.getDate()).padStart(2, '0')
            const hora = String(d.getHours()).padStart(2, '0')
            const min = String(d.getMinutes()).padStart(2, '0')
            const seg = String(d.getSeconds()).padStart(2, '0')
            return `${anio}-${mes}-${dia} ${hora}:${min}:${seg}`
        },

        // ============================================================
        // EXPORTAR A EXCEL
        // ============================================================
        async exportarExcel() {
            if (this.trazabilidadList.length === 0) {
                this.$toast.warning('No hay datos para exportar')
                return
            }

            try {
                this.isLoading = true

                const datosExportar = this.itemsFiltrados

                if (!Array.isArray(datosExportar) || datosExportar.length === 0) {
                    this.$toast.warning('No hay datos para exportar')
                    return
                }

                const ahora = new Date()
                const anio = ahora.getFullYear()
                const mes = String(ahora.getMonth() + 1).padStart(2, '0')
                const dia = String(ahora.getDate()).padStart(2, '0')
                const fechaStr = `${anio}-${mes}-${dia}`

                const { Workbook } = XLSX
                const workbook = new Workbook()
                const worksheet = workbook.addWorksheet("Trazabilidad")

                const styles = {
                    tituloPrincipal: {
                        font: { name: "Calibri", size: 16, bold: true },
                        alignment: { horizontal: "center", vertical: "middle" },
                        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } },
                        border: {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        }
                    },
                    subtitulo: {
                        font: { name: "Calibri", size: 11, bold: true },
                        alignment: { horizontal: "center", vertical: "middle" },
                        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } },
                        border: {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        }
                    },
                    encabezado: {
                        font: { name: "Calibri", size: 11, bold: true, color: { argb: "000000" } },
                        alignment: { horizontal: "center", vertical: "middle" },
                        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "F2F2F2" } },
                        border: {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        }
                    },
                    datoNormal: {
                        font: { name: "Calibri", size: 10 },
                        alignment: { horizontal: "left", vertical: "middle", wrapText: true },
                        border: {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        }
                    }
                }

                const HEADER_ROW = [
                    'Fecha',
                    'Usuario',
                    'Email',
                    'Campo',
                    'Valor Antes',
                    'Valor Después',
                    'Comentario'
                ]

                const headerColumns = HEADER_ROW.length

                worksheet.mergeCells(`A1:${String.fromCharCode(64 + headerColumns)}1`)
                worksheet.getCell('A1').value = 'TRAZABILIDAD DEL SISTEMA'
                Object.assign(worksheet.getCell('A1'), styles.tituloPrincipal)
                worksheet.getCell('A1').font.color = { argb: "000000" }

                worksheet.mergeCells(`A2:${String.fromCharCode(64 + headerColumns)}2`)
                worksheet.getCell('A2').value = `Generado: ${fechaStr} por ${this.$store.state.userName || 'Usuario'}`
                Object.assign(worksheet.getCell('A2'), styles.subtitulo)

                worksheet.addRow([])

                const headerRow = worksheet.addRow(HEADER_ROW)
                headerRow.eachCell((cell) => {
                    Object.assign(cell, styles.encabezado)
                })

                worksheet.columns = [
                    { width: 20 },
                    { width: 25 },
                    { width: 30 },
                    { width: 20 },
                    { width: 45 },
                    { width: 45 },
                    { width: 50 }
                ]

                datosExportar.forEach((fila) => {
                    const v_fila = [
                        this.formatFecha(fila.fecha),
                        fila.user_nombre || '',
                        fila.user_email || '',
                        fila.campo || '',
                        fila.valor_antes || '',
                        fila.valor_despues || '',
                        fila.comentario || ''
                    ]

                    const currentRow = worksheet.addRow(v_fila)
                    currentRow.eachCell((cell) => {
                        Object.assign(cell, styles.datoNormal)
                    })
                })

                worksheet.views = [
                    { state: 'frozen', ySplit: 4 }
                ]

                worksheet.autoFilter = {
                    from: { row: 4, column: 1 },
                    to: { row: 4, column: headerColumns }
                }

                const file = await workbook.xlsx.writeBuffer()
                const downloadLink = document.createElement("a")
                downloadLink.download = `Trazabilidad_${fechaStr}.xlsx`
                downloadLink.href = window.URL.createObjectURL(
                    new Blob([file], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    })
                )
                downloadLink.style.display = "none"
                document.body.appendChild(downloadLink)
                downloadLink.click()
                downloadLink.remove()

                this.$toast.success('Excel exportado exitosamente')

            } catch (error) {
                console.error('Error al exportar Excel:', error)
                this.$toast.error('Error al exportar el Excel: ' + error.message)
            } finally {
                this.isLoading = false
            }
        }
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
    flex-wrap: wrap;
}

.niif-header .filter-label {
    font-weight: 500;
    color: #495057;
    margin-bottom: 0;
    white-space: nowrap;
}

.niif-header .filter-date {
    min-width: 160px;
    max-width: 180px;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
    width: auto;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

.niif-header .clear-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.kardex-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-refresh-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #6c757d;
    color: white;
    border: 1px solid #6c757d;
}

.table-toolbar-refresh-btn:hover:not(:disabled) {
    background-color: #5a6268;
    border-color: #5a6268;
}

.table-toolbar-refresh-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.table-toolbar-export-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #1f7b4a !important;
    color: white !important;
    border: 1px solid #1f7b4a !important;
}

.table-toolbar-export-btn:hover:not(:disabled) {
    background-color: #16633a !important;
    border-color: #16633a !important;
}

.table-toolbar-export-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* ===== Celdas de la tabla ===== */
.campo-badge {
    display: inline-block;
    padding: 2px 8px;
    background-color: #eef2f7;
    border: 1px solid #d6dee7;
    border-radius: 4px;
    font-size: 12px;
    color: #2c3e50;
    font-weight: 500;
    white-space: nowrap;
}

.valor-cell {
    max-width: 320px;
    white-space: normal;
    word-break: break-word;
    font-size: 12px;
    line-height: 1.35;
    color: #333;
}

.comentario-cell {
    max-width: 280px;
    white-space: normal;
    word-break: break-word;
    font-size: 12px;
    line-height: 1.35;
    color: #444;
    font-style: italic;
}

/* Responsive */
@media (max-width: 1200px) {
    .niif-header .filter-date {
        min-width: 140px;
        max-width: 160px;
        height: 36px;
        font-size: 13px;
    }

    .niif-header .consult-btn,
    .niif-header .clear-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }

    .valor-cell {
        max-width: 240px;
    }

    .comentario-cell {
        max-width: 200px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }

    .niif-header .filter-date {
        min-width: 130px;
        max-width: 160px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }

    .niif-header .consult-btn,
    .niif-header .clear-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }

    .valor-cell {
        max-width: 200px;
    }

    .comentario-cell {
        max-width: 180px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }

    .niif-header .filter-date {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }

    .niif-header .filters-container {
        gap: 4px;
    }

    .niif-header .filter-label {
        flex: 0 0 100%;
        margin-bottom: 2px;
    }

    .niif-header .consult-btn,
    .niif-header .clear-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }

    .kardex-toolbar-right {
        flex-wrap: wrap;
        width: 100%;
    }

    .kardex-toolbar-right .table-toolbar-refresh-btn,
    .kardex-toolbar-right .table-toolbar-export-btn {
        flex: 1 1 auto;
        min-width: 80px;
    }
}
</style>